import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PageConfig } from '@app/models/page-config';
import { PageContent } from '@app/models/page-content';
import { Observable, shareReplay } from 'rxjs';
import { environment } from '@environments/environment';

const baseAppEndpointsUrls = {
	home: `${environment.baseAPIUrl}/home`,
	ARS: `${environment.baseAPIUrl}/ars`,
	limiti: `${environment.baseAPIUrl}/limiti`,
	flussi: `${environment.baseAPIUrl}/flussi`,
	common: `${environment.baseAPIUrl}/common`,
};

const APIUrls = {
	home: {
		pageContent: baseAppEndpointsUrls.home,
	},
	ARS: {
		pageConfig: baseAppEndpointsUrls.ARS,
		report: baseAppEndpointsUrls.common + '/report',
	},
	limiti: {
		pageConfig: baseAppEndpointsUrls.limiti,
		report: baseAppEndpointsUrls.common + '/report',
	},
	flussi: {
		pageConfig: baseAppEndpointsUrls.flussi,
		report: baseAppEndpointsUrls.flussi + '/report',
	},
};

interface EndpointWrapper {
	getPageContent: Observable<PageContent>;
}

interface AppEndpointWrapper {
	getPageConfig: Observable<PageConfig>;
	getReport: (params: HttpParams) => Observable<Blob>;
}

@Injectable({
	providedIn: 'root',
})
export class ApiCallerService {
	private homeEndpointWrapper: EndpointWrapper = {} as EndpointWrapper;
	private arsEndpointWrapper: AppEndpointWrapper = {} as AppEndpointWrapper;
	private limitiEndpointWrapper: AppEndpointWrapper = {} as AppEndpointWrapper;
	private flussiEndpointWrapper: AppEndpointWrapper = {} as AppEndpointWrapper;

	constructor(http: HttpClient) {
		this.homeEndpointWrapper = {
			getPageContent: http.get<PageContent>(APIUrls.home.pageContent).pipe(shareReplay()),
		};

		this.arsEndpointWrapper = {
			getPageConfig: http.get<PageConfig>(APIUrls.ARS.pageConfig).pipe(shareReplay()),
			getReport: (params: HttpParams) =>
				http.get(APIUrls.ARS.report, {
					responseType: 'blob',
					params: params,
				}),
		};

		this.limitiEndpointWrapper = {
			getPageConfig: http.get<PageConfig>(APIUrls.limiti.pageConfig).pipe(shareReplay()),
			getReport: (params: HttpParams) =>
				http.get(APIUrls.limiti.report, {
					responseType: 'blob',
					params: params,
				}),
		};

		this.flussiEndpointWrapper = {
			getPageConfig: http.get<PageConfig>(APIUrls.flussi.pageConfig).pipe(shareReplay()),
			getReport: (params: HttpParams) =>
				http.get(APIUrls.flussi.report, {
					responseType: 'blob',
					params: params,
				}),
		};
	}

	/** HOME */

	/**
	 * Calls api to get Home's content
	 * @param next callback called at response received
	 * @param error callback called if errors in response
	 * @param complete callback called if response successful
	 */
	getHomePageContent = (next?: Function, error?: Function, complete?: Function) => {
		this.homeEndpointWrapper.getPageContent.subscribe({
			next: (data) => (next ? next(data) : {}),
			error: () => (error ? error() : {}),
			complete: () => (complete ? complete() : {}),
		});
	};

	/** ARS */

	/**
	 * Calls api to get ARS' page configuration (content + filters)
	 * @param next callback called at response received
	 * @param error callback called if errors in response
	 * @param complete callback called if response successful
	 */
	getARSPageConfig = (next?: Function, error?: Function, complete?: Function) => {
		this.arsEndpointWrapper.getPageConfig.subscribe({
			next: (data) => (next ? next(data) : {}),
			error: () => (error ? error() : {}),
			complete: () => (complete ? complete() : {}),
		});
	};

	/**
	 * Calls api to get ARS' report
	 * @param params query parameters for GET request (report format and fitlers values)
	 * @param next callback called at response received
	 * @param error callback called if errors in response
	 * @param complete callback called if response successful
	 */
	getARSReport = (params: HttpParams, next?: Function, error?: Function, complete?: Function) => {
		this.arsEndpointWrapper.getReport(params).subscribe({
			next: (data) => (next ? next(data) : {}),
			error: () => (error ? error() : {}),
			complete: () => (complete ? complete() : {}),
		});
	};

	/** LIMITI */

	/**
	 * Calls api to get Limiti's page configuration (content + filters)
	 * @param next callback called at response received
	 * @param error callback called if errors in response
	 * @param complete callback called if response successful
	 */
	getLimitiPageConfig = (next?: Function, error?: Function, complete?: Function) => {
		this.limitiEndpointWrapper.getPageConfig.subscribe({
			next: (data) => (next ? next(data) : {}),
			error: () => (error ? error() : {}),
			complete: () => (complete ? complete() : {}),
		});
	};

	/**
	 * Calls api to get Limiti's report
	 * @param params query parameters for GET request (report format and fitlers values)
	 * @param next callback called at response received
	 * @param error callback called if errors in response
	 * @param complete callback called if response successful
	 */
	getLimitiReport = (params: HttpParams, next?: Function, error?: Function, complete?: Function) => {
		this.limitiEndpointWrapper.getReport(params).subscribe({
			next: (data) => (next ? next(data) : {}),
			error: () => (error ? error() : {}),
			complete: () => (complete ? complete() : {}),
		});
	};

	/** FLUSSI */

	/**
	 * Calls api to get Flussi's page configuration (content + filters)
	 * @param next callback called at response received
	 * @param error callback called if errors in response
	 * @param complete callback called if response successful
	 */
	getFlussiPageConfig = (next?: Function, error?: Function, complete?: Function) => {
		this.flussiEndpointWrapper.getPageConfig.subscribe({
			next: (data) => (next ? next(data) : {}),
			error: () => (error ? error() : {}),
			complete: () => (complete ? complete() : {}),
		});
	};

	/**
	 * Calls api to get Flussi's report
	 * @param params query parameters for GET request (report format and fitlers values)
	 * @param next callback called at response received
	 * @param error callback called if errors in response
	 * @param complete callback called if response successful
	 */
	getFlussiReport = (params: HttpParams, next?: Function, error?: Function, complete?: Function) => {
		this.flussiEndpointWrapper.getReport(params).subscribe({
			next: (data) => (next ? next(data) : {}),
			error: () => (error ? error() : {}),
			complete: () => (complete ? complete() : {}),
		});
	};
}
